import React, { useState, useEffect, useRef } from 'react';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const COP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);
const COMISION = 0.038;

// ── Parser Davivienda ─────────────────────────────────────
function parsearDavivienda(texto, mesAnio) {
  const movimientos = [];
  const lineas = texto.split('\n');
  const [anio, mes] = mesAnio.split('-');
  for (const linea of lineas) {
    const l = linea.replace(/\s+/g, ' ').trim();
    const re = /^(\d{2})\s+\d{2}\s+\d+\s+(.+?)\s+\d{5,}\s+\$\s*0[.,]00\s+\$\s*([\d,]+(?:\.\d{2})?)$/;
    const m = l.match(re);
    if (!m) continue;
    const dia = parseInt(m[1]);
    if (dia < 1 || dia > 31) continue;
    const desc = m[2].trim().toUpperCase();
    const monto = parseFloat(m[3].replace(/,/g, ''));
    if (isNaN(monto) || monto <= 0) continue;
    const fecha = `${anio}-${mes}-${String(dia).padStart(2, '0')}`;
    let tipo = null;
    if (desc.match(/LLAVE|COMERCIO CON LLAVE/)) tipo = 'qr';
    else if (desc.match(/MASTERCARD/)) tipo = 'mastercard';
    else if (desc.match(/\bVISA\b/)) tipo = 'visa';
    else if (desc.match(/AMEX|AMERICAN/)) tipo = 'amex';
    if (tipo) movimientos.push({ fecha, descripcion: desc.substring(0, 100), tipo, monto });
  }
  return movimientos;
}

// ── Extraer texto de PDF ──────────────────────────────────
async function extraerTextoPDF(file) {
  if (!window.pdfjsLib) {
    await new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let texto = '';
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    const lineas = {};
    content.items.forEach(item => {
      const y = Math.round(item.transform[5]);
      if (!lineas[y]) lineas[y] = [];
      lineas[y].push({ x: item.transform[4], text: item.str });
    });
    Object.keys(lineas).sort((a, b) => b - a).forEach(y => {
      texto += lineas[y].sort((a, b) => a.x - b.x).map(i => i.text).join(' ') + '\n';
    });
  }
  return texto;
}


// ── Panel de un PDV ───────────────────────────────────────
function PanelPDV({ bodega, mesActual }) {
  const fileRef    = useRef();
  const csvRef     = useRef();
  const [procesando, setProcesando]       = useState(false);
  const [procesandoCSV, setProcesandoCSV] = useState(false);
  const [importando, setImportando]       = useState(false);
  const [importandoCSV, setImportandoCSV] = useState(false);
  const [preview, setPreview]             = useState(null);
  const [previewCSV, setPreviewCSV]       = useState(null);
  const [datos, setDatos]                 = useState(null);
  const [cargando, setCargando]           = useState(false);
  const [subVista, setSubVista]           = useState('conciliacion');
  const csvDfRef                          = useRef();
  const csvRappiRef                       = useRef();
  const hoy = format(new Date(), 'yyyy-MM-dd');
  const [procesandoDf, setProcesandoDf]   = useState(false);
  const [procesandoRappi, setProcesandoRappi] = useState(false);
  const [importandoRappi, setImportandoRappi] = useState(false);
  const [previewRappi, setPreviewRappi]   = useState(null);
  const [datosRappi, setDatosRappi]       = useState(null);
  const [cargandoRappi, setCargandoRappi] = useState(false);
  const [desdeRappi, setDesdeRappi]       = useState(hoy);
  const [hastaRappi, setHastaRappi]       = useState(hoy);
  const [importandoDf, setImportandoDf]   = useState(false);
  const [previewDf, setPreviewDf]         = useState(null);
  const [datosDf, setDatosDf]             = useState(null);
  const [cargandoDf, setCargandoDf]       = useState(false);
  const [desdeDf, setDesdeDf]             = useState(hoy);
  const [hastaDf, setHastaDf]             = useState(hoy);

  useEffect(() => {
    cargar();
  }, [mesActual, bodega.id]);

  const cargar = () => {
    setCargando(true);
    setDatos(null);
    API.get('/extracto/conciliacion', { params: { mes: mesActual, bodega_id: bodega.id } })
      .then(r => setDatos(r.data))
      .catch(() => toast.error('Error cargando conciliación'))
      .finally(() => setCargando(false));
  };

  const cargarDatafono = () => {
    setCargandoDf(true);
    setDatosDf(null);
    API.get('/datafono/resumen', { params: { bodega_id: bodega.id, desde: desdeDf, hasta: hastaDf } })
      .then(r => setDatosDf(r.data))
      .catch(() => toast.error('Error cargando datáfono'))
      .finally(() => setCargandoDf(false));
  };

  const handlePDF = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcesando(true);
    setPreview(null);
    try {
      const texto = await extraerTextoPDF(file);
      const movimientos = parsearDavivienda(texto, mesActual);
      const resumen = { qr: 0, mastercard: 0, visa: 0, amex: 0, count: movimientos.length };
      movimientos.forEach(m => { resumen[m.tipo] = (resumen[m.tipo] || 0) + m.monto; });
      setPreview({ movimientos, resumen });
      if (movimientos.length === 0) toast.error('No se encontraron movimientos. Verifica el formato.');
      else toast.success(`${movimientos.length} movimientos detectados`);
    } catch (err) {
      toast.error('Error procesando PDF: ' + err.message);
    } finally {
      setProcesando(false);
      e.target.value = '';
    }
  };

  const handleImportar = async () => {
    if (!preview?.movimientos?.length) return;
    setImportando(true);
    try {
      const r = await API.post('/extracto/importar', {
        movimientos: preview.movimientos,
        mes: mesActual,
        bodega_id: bodega.id,
      });
      toast.success(`✅ ${r.data.insertados} movimientos importados`);
      setPreview(null);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al importar');
    } finally {
      setImportando(false);
    }
  };

  const handleCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcesandoCSV(true);
    setPreviewCSV(null);
    try {
      const texto = await file.text();
      const movimientos = parsearCSVDatafono(texto);
      const resumen = { qr: 0, mastercard: 0, visa: 0, amex: 0, comision: 0, count: movimientos.length };
      movimientos.forEach(m => {
        resumen[m.tipo] = (resumen[m.tipo] || 0) + m.monto;
        resumen.comision += m.comision;
      });
      setPreviewCSV({ movimientos, resumen });
      if (movimientos.length === 0) toast.error('No se encontraron transacciones. Verifica el formato.');
      else toast.success(`${movimientos.length} transacciones detectadas`);
    } catch (err) {
      toast.error('Error procesando CSV: ' + err.message);
    } finally {
      setProcesandoCSV(false);
      e.target.value = '';
    }
  };

  const handleImportarCSV = async () => {
    if (!previewCSV?.movimientos?.length) return;
    setImportandoCSV(true);
    try {
      const r = await API.post('/extracto/importar', {
        movimientos: previewCSV.movimientos,
        mes: mesActual,
        bodega_id: bodega.id,
      });
      toast.success(`✅ ${r.data.insertados} transacciones importadas`);
      setPreviewCSV(null);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al importar');
    } finally {
      setImportandoCSV(false);
    }
  };

  const handleCSVDatafono = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcesandoDf(true);
    setPreviewDf(null);
    try {
      const texto = await file.text();
      const movs = parsearCSVDatafono(texto);
      // Enriquecer con franquicia original
      const lineas = texto.split('\n');
      const headers = lineas[0].split('|').map(h => h.trim());
      const iFranq = headers.findIndex(h => h.includes('FRANQUICIA'));
      const transacciones = movs.map((m, i) => ({
        ...m,
        franquicia: lineas[i+1]?.split('|')[iFranq]?.trim() || m.tipo.toUpperCase()
      }));
      const resumen = { qr: 0, tarjeta: 0, comision: 0, count: transacciones.length };
      transacciones.forEach(t => {
        if (t.tipo === 'qr') resumen.qr += t.monto;
        else resumen.tarjeta += t.monto;
        resumen.comision += t.comision;
      });
      setPreviewDf({ transacciones, resumen });
      if (transacciones.length === 0) toast.error('No se encontraron transacciones.');
      else toast.success(`${transacciones.length} transacciones detectadas`);
    } catch(err) {
      toast.error('Error procesando CSV: ' + err.message);
    } finally {
      setProcesandoDf(false);
      e.target.value = '';
    }
  };

  const handleImportarDatafono = async () => {
    if (!previewDf?.transacciones?.length) return;
    setImportandoDf(true);
    try {
      const r = await API.post('/datafono/importar', {
        transacciones: previewDf.transacciones,
        bodega_id: bodega.id,
      });
      toast.success(`✅ ${r.data.insertados} transacciones importadas (${r.data.desde} → ${r.data.hasta})`);
      setPreviewDf(null);
      // Auto-ajustar rango y cargar
      setDesdeDf(r.data.desde);
      setHastaDf(r.data.hasta);
      setTimeout(cargarDatafono, 300);
    } catch(e) {
      toast.error(e.response?.data?.error || 'Error al importar');
    } finally {
      setImportandoDf(false);
    }
  };

  const cargarRappi = () => {
    setCargandoRappi(true);
    setDatosRappi(null);
    API.get('/rappi/resumen', { params: { bodega_id: bodega.id, desde: desdeRappi, hasta: hastaRappi } })
      .then(r => setDatosRappi(r.data))
      .catch(() => toast.error('Error cargando Rappi'))
      .finally(() => setCargandoRappi(false));
  };

  const parsearExcelRappi = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          // Parsear manualmente como TSV/CSV con cabecera en fila 1
          const text = new TextDecoder('utf-8').decode(data);
          const lineas = text.split('\n').filter(l => l.trim());
          // Detectar separador
          const sep = lineas[0].includes('\t') ? '\t' : ',';
          const headers = lineas[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ''));
          const iOrden   = headers.findIndex(h => h.toLowerCase().includes('orden'));
          const iCliente = headers.findIndex(h => h.toLowerCase().includes('cliente'));
          const iSucursal= headers.findIndex(h => h.toLowerCase().includes('sucursal'));
          const iFecha   = headers.findIndex(h => h.toLowerCase().includes('fecha'));
          const iMetodo  = headers.findIndex(h => h.toLowerCase().includes('método') || h.toLowerCase().includes('metodo') || h.toLowerCase().includes('pago'));
          const iTotal   = headers.findIndex(h => h.toLowerCase().includes('total'));
          const iEstado  = headers.findIndex(h => h.toLowerCase().includes('estado'));
          const ordenes = [];
          for (let i = 1; i < lineas.length; i++) {
            const cols = lineas[i].split(sep).map(c => c.trim().replace(/^"|"$/g, ''));
            const orden_id = cols[iOrden]?.trim();
            if (!orden_id || orden_id === 'Orden') continue;
            const total = parseFloat(cols[iTotal]) || 0;
            if (total <= 0) continue;
            ordenes.push({
              orden_id,
              cliente: cols[iCliente]?.replace(' null','').trim() || '',
              sucursal_rappi: cols[iSucursal]?.trim() || '',
              fecha: cols[iFecha]?.trim() || '',
              metodo_pago: cols[iMetodo]?.trim() || '',
              total,
              estado: cols[iEstado]?.trim() || 'Terminada',
            });
          }
          resolve(ordenes);
        } catch(err) { reject(err); }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleExcelRappi = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcesandoRappi(true);
    setPreviewRappi(null);
    try {
      // Usar SheetJS si está disponible, si no parsear como texto
      let ordenes = [];
      const texto = await file.text();
      const lineas = texto.split('\n').filter(l => l.trim());
      const sep = lineas[0].includes('\t') ? '\t' : ',';
      const headers = lineas[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ''));
      const iOrden    = headers.findIndex(h => /orden/i.test(h));
      const iCliente  = headers.findIndex(h => /cliente/i.test(h));
      const iSucursal = headers.findIndex(h => /sucursal/i.test(h));
      const iFecha    = headers.findIndex(h => /fecha/i.test(h));
      const iMetodo   = headers.findIndex(h => /m.todo|pago/i.test(h));
      const iTotal    = headers.findIndex(h => /total/i.test(h));
      const iEstado   = headers.findIndex(h => /estado/i.test(h));
      for (let i = 1; i < lineas.length; i++) {
        const cols = lineas[i].split(sep).map(c => c.trim().replace(/^"|"$/g, ''));
        const orden_id = cols[iOrden]?.trim();
        if (!orden_id) continue;
        const total = parseFloat(cols[iTotal]) || 0;
        if (total <= 0) continue;
        ordenes.push({
          orden_id,
          cliente: cols[iCliente]?.replace(' null','').trim() || '',
          sucursal_rappi: cols[iSucursal]?.trim() || '',
          fecha: cols[iFecha]?.trim() || '',
          metodo_pago: cols[iMetodo]?.trim() || '',
          total,
          estado: cols[iEstado]?.trim() || 'Terminada',
        });
      }
      const resumen = { total: ordenes.reduce((s,o) => s + o.total, 0), count: ordenes.length };
      setPreviewRappi({ ordenes, resumen });
      if (ordenes.length === 0) toast.error('No se encontraron órdenes.');
      else toast.success(`${ordenes.length} órdenes detectadas`);
    } catch(err) {
      toast.error('Error procesando archivo: ' + err.message);
    } finally {
      setProcesandoRappi(false);
      e.target.value = '';
    }
  };

  const handleImportarRappi = async () => {
    if (!previewRappi?.ordenes?.length) return;
    setImportandoRappi(true);
    try {
      const r = await API.post('/rappi/importar', { ordenes: previewRappi.ordenes, bodega_id: bodega.id });
      toast.success(`✅ ${r.data.insertadas} nuevas, ${r.data.actualizadas} actualizadas`);
      setPreviewRappi(null);
      setTimeout(cargarRappi, 300);
    } catch(e) {
      toast.error(e.response?.data?.error || 'Error al importar');
    } finally {
      setImportandoRappi(false);
    }
  };

  const diasConDatos = (datos?.dias || []).filter(d =>
    d.extracto.tarjeta_total > 0 || d.extracto.qr > 0 ||
    d.sistema.tarjeta_bruta > 0 || d.sistema.qr > 0
  );

  return (
    <div>
      {/* Panel importar */}
      <div style={{ background: 'white', borderRadius: 14, padding: '18px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#374151' }}>
          📄 Extracto {bodega.nombre} — {mesActual}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{
            padding: '9px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, display: 'inline-block',
            cursor: procesando ? 'not-allowed' : 'pointer',
            background: procesando ? '#E5E7EB' : 'linear-gradient(135deg,#1a1a2e,#16213e)',
            color: procesando ? '#9CA3AF' : 'white'
          }}>
            {procesando ? '⏳ Procesando...' : '📤 Subir PDF extracto'}
            <input ref={fileRef} type="file" accept=".pdf" onChange={handlePDF}
              style={{ display: 'none' }} disabled={procesando} />
          </label>
          {!cargando && (
            datos?.tiene_extracto
              ? <span style={{ fontSize: 12, color: '#059669', fontWeight: 600, background: '#D1FAE5', padding: '6px 12px', borderRadius: 8 }}>✅ Extracto importado</span>
              : <span style={{ fontSize: 12, color: '#92400E', fontWeight: 600, background: '#FEF3C7', padding: '6px 12px', borderRadius: 8 }}>⚠️ Sin extracto para este mes</span>
          )}
        </div>

        {/* Botón CSV datáfono */}
        <div style={{ marginTop: 14, borderTop: '1px solid #F3F4F6', paddingTop: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 8 }}>
            📊 Revisión diaria — CSV datáfono Davivienda
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{
              padding: '9px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, display: 'inline-block',
              cursor: procesandoCSV ? 'not-allowed' : 'pointer',
              background: procesandoCSV ? '#E5E7EB' : 'linear-gradient(135deg,#0369A1,#0284C7)',
              color: procesandoCSV ? '#9CA3AF' : 'white'
            }}>
              {procesandoCSV ? '⏳ Procesando...' : '📂 Subir CSV datáfono'}
              <input ref={csvRef} type="file" accept=".csv,.CSV" onChange={handleCSV}
                style={{ display: 'none' }} disabled={procesandoCSV} />
            </label>
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>Archivo ARTHGELTU...CSV del banco</span>
          </div>
        </div>

        {/* Preview CSV */}
        {previewCSV && (
          <div style={{ marginTop: 14, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1E40AF', marginBottom: 10 }}>
              📊 {previewCSV.movimientos.length} transacciones detectadas
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              {[
                { label: '📱 P2M / QR', val: previewCSV.resumen.qr, color: '#0EA5E9' },
                { label: '💳 Mastercard', val: previewCSV.resumen.mastercard, color: '#2563EB' },
                { label: '💳 Visa', val: previewCSV.resumen.visa, color: '#1D4ED8' },
                { label: '💳 Amex', val: previewCSV.resumen.amex, color: '#7C3AED' },
              ].filter(x => x.val > 0).map((x, i) => (
                <div key={i} style={{ background: 'white', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                  <span style={{ color: '#6B7280' }}>{x.label}: </span>
                  <strong style={{ color: x.color, fontFamily: 'monospace' }}>{COP(x.val)}</strong>
                </div>
              ))}
              {previewCSV.resumen.comision > 0 && (
                <div style={{ background: 'white', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                  <span style={{ color: '#6B7280' }}>Comisión banco: </span>
                  <strong style={{ color: '#EF4444', fontFamily: 'monospace' }}>{COP(previewCSV.resumen.comision)}</strong>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleImportarCSV} disabled={importandoCSV} style={{
                padding: '8px 20px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
                cursor: importandoCSV ? 'not-allowed' : 'pointer',
                background: 'linear-gradient(135deg,#0369A1,#0284C7)', color: 'white'
              }}>
                {importandoCSV ? '⏳ Importando...' : '💾 Confirmar e importar'}
              </button>
              <button onClick={() => setPreviewCSV(null)} style={{
                padding: '8px 16px', border: '2px solid #E5E7EB', borderRadius: 8,
                background: 'white', cursor: 'pointer', fontSize: 13, color: '#6B7280', fontWeight: 600
              }}>Cancelar</button>
            </div>
          </div>
        )}

        {/* Preview PDF */}
        {preview && (
          <div style={{ marginTop: 14, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#065F46', marginBottom: 10 }}>
              ✅ {preview.movimientos.length} movimientos detectados
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              {[
                { label: '📱 QR / Llave', val: preview.resumen.qr, color: '#0EA5E9' },
                { label: '💳 Mastercard', val: preview.resumen.mastercard, color: '#2563EB' },
                { label: '💳 Visa', val: preview.resumen.visa, color: '#1D4ED8' },
                { label: '💳 Amex', val: preview.resumen.amex, color: '#7C3AED' },
              ].filter(x => x.val > 0).map((x, i) => (
                <div key={i} style={{ background: 'white', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                  <span style={{ color: '#6B7280' }}>{x.label}: </span>
                  <strong style={{ color: x.color, fontFamily: 'monospace' }}>{COP(x.val)}</strong>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleImportar} disabled={importando} style={{
                padding: '8px 20px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
                cursor: importando ? 'not-allowed' : 'pointer',
                background: 'linear-gradient(135deg,#059669,#047857)', color: 'white'
              }}>
                {importando ? '⏳ Importando...' : '💾 Confirmar e importar'}
              </button>
              <button onClick={() => setPreview(null)} style={{
                padding: '8px 16px', border: '2px solid #E5E7EB', borderRadius: 8,
                background: 'white', cursor: 'pointer', fontSize: 13, color: '#6B7280', fontWeight: 600
              }}>Cancelar</button>
            </div>
          </div>
        )}
      </div>

      {/* Totales resumen */}
      {datos?.tiene_extracto && datos?.totales && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(165px,1fr))', gap: 10, marginBottom: 16 }}>
          {[
            { label: 'Tarjeta sistema bruta', val: datos.totales.sis_tarjeta_neta / (1 - COMISION), color: '#9CA3AF', icon: '💳' },
            { label: 'Tarjeta sistema (-3.8%)', val: datos.totales.sis_tarjeta_neta, color: '#2563EB', icon: '💳' },
            { label: 'Tarjeta en extracto', val: datos.totales.ext_tarjeta, color: '#1D4ED8', icon: '🏦' },
            { label: 'Diferencia tarjeta', val: datos.totales.dif_tarjeta, color: Math.abs(datos.totales.dif_tarjeta) < 10000 ? '#059669' : '#EF4444', icon: '📊' },
            { label: 'QR sistema', val: datos.totales.sis_qr, color: '#0EA5E9', icon: '📱' },
            { label: 'QR en extracto', val: datos.totales.ext_qr, color: '#0284C7', icon: '🏦' },
            { label: 'Diferencia QR', val: datos.totales.dif_qr, color: Math.abs(datos.totales.dif_qr) < 5000 ? '#059669' : '#EF4444', icon: '📊' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 10, padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 16, marginBottom: 3 }}>{s.icon}</div>
              <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 14, color: s.color }}>{COP(s.val)}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 3, marginBottom: 14, background: '#F3F4F6', borderRadius: 8, padding: 3, width: 'fit-content' }}>
          {[{ id: 'conciliacion', label: '⚖️ Conciliación' }, { id: 'extracto', label: '🏦 Extracto' }].map(t => (
            <button key={t.id} onClick={() => setSubVista(t.id)} style={{
              padding: '6px 16px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12,
              fontWeight: subVista === t.id ? 700 : 500,
              background: subVista === t.id ? 'white' : 'transparent',
              color: subVista === t.id ? '#111827' : '#6B7280',
              boxShadow: subVista === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}>{t.label}</button>
          ))}
        </div>

      {cargando && <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>⏳ Calculando...</div>}

      {/* Tabla conciliación */}
      {datos?.tiene_extracto && subVista === 'conciliacion' && (
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ background: '#1a1a2e', padding: '11px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>
              ⚖️ Conciliación día a día — {bodega.nombre} — {mesActual}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
              Tarjeta: venta X → extracto X+1 · QR: mismo día
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['Fecha venta', '# Trx sistema', '# Trx banco', 'Tarjeta sistema', 'Tarjeta (-3.8%)', 'Extracto tarjeta *día sig.', 'Dif. tarjeta', 'QR sistema', 'Extracto QR *mismo día', 'Dif. QR'].map(h => (
                    <th key={h} style={{ padding: '9px 11px', textAlign: h.startsWith('Fecha') ? 'left' : 'right', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {diasConDatos.map((d, i) => {
                  const alerta = !d.diferencia.cuadra_tarjeta || !d.diferencia.cuadra_qr;
                  return (
                    <tr key={d.fecha} style={{ borderTop: '1px solid #F3F4F6', background: alerta ? '#FFFBEB' : i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                      <td style={{ padding: '9px 11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {format(new Date(d.fecha + 'T12:00'), "d MMM", { locale: es })}
                        <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 400 }}>
                          {format(new Date(d.fecha + 'T12:00'), 'EEEE', { locale: es })}
                        </div>
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#6B7280' }}>
                        {d.sistema.trx_tarjeta > 0 || d.sistema.trx_qr > 0
                          ? <span>{(d.sistema.trx_tarjeta||0) + (d.sistema.trx_qr||0)}</span>
                          : '—'}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#0369A1' }}>
                        {d.extracto.trx_count > 0 ? d.extracto.trx_count : '—'}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#9CA3AF' }}>
                        {d.sistema.tarjeta_bruta > 0 ? COP(d.sistema.tarjeta_bruta) : '—'}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#2563EB', fontWeight: 600 }}>
                        {d.sistema.tarjeta_neta > 0 ? COP(d.sistema.tarjeta_neta) : '—'}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#1D4ED8', fontWeight: 700 }}>
                        {d.extracto.tarjeta_total > 0 ? COP(d.extracto.tarjeta_total) : '—'}
                        {d.extracto.tarjeta_total > 0 && (
                          <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 400 }}>
                            <span style={{ color: '#BAE6FD' }}>extracto {d.fecha_extracto_tarjeta ? format(new Date(d.fecha_extracto_tarjeta + 'T12:00'), 'd MMM', { locale: es }) : ''}</span>
                            <br />
                            {d.extracto.mastercard > 0 ? `MC ${COP(d.extracto.mastercard)} ` : ''}
                            {d.extracto.visa > 0 ? `VS ${COP(d.extracto.visa)} ` : ''}
                            {d.extracto.amex > 0 ? `AX ${COP(d.extracto.amex)}` : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right' }}>
                        <DifBadge val={d.diferencia.tarjeta} cuadra={d.diferencia.cuadra_tarjeta} />
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#0EA5E9' }}>
                        {d.sistema.qr > 0 ? COP(d.sistema.qr) : '—'}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#0284C7', fontWeight: 700 }}>
                        {d.extracto.qr > 0 ? COP(d.extracto.qr) : '—'}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right' }}>
                        <DifBadge val={d.diferencia.qr} cuadra={d.diferencia.cuadra_qr} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {datos.totales && diasConDatos.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #E5E7EB', background: '#F9FAFB' }}>
                    <td style={{ padding: '10px 11px', fontWeight: 800, fontSize: 13 }}>Total mes</td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#6B7280' }}>
                      {datos.totales.total_trx_sis || '—'}
                    </td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#0369A1' }}>
                      {datos.totales.total_trx_banco || '—'}
                    </td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#9CA3AF' }}>
                      {COP(datos.totales.sis_tarjeta_neta / (1 - COMISION))}
                    </td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#2563EB' }}>
                      {COP(datos.totales.sis_tarjeta_neta)}
                    </td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#1D4ED8' }}>
                      {COP(datos.totales.ext_tarjeta)}
                    </td>
                    <td style={{ padding: '10px 11px', textAlign: 'right' }}>
                      <DifBadge val={datos.totales.dif_tarjeta} cuadra={Math.abs(datos.totales.dif_tarjeta) < 10000} />
                    </td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#0EA5E9' }}>
                      {COP(datos.totales.sis_qr)}
                    </td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#0284C7' }}>
                      {COP(datos.totales.ext_qr)}
                    </td>
                    <td style={{ padding: '10px 11px', textAlign: 'right' }}>
                      <DifBadge val={datos.totales.dif_qr} cuadra={Math.abs(datos.totales.dif_qr) < 5000} />
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
            {diasConDatos.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                Sin movimientos para este mes en {bodega.nombre}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vista extracto */}
      {datos?.tiene_extracto && subVista === 'extracto' && (
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ background: '#1a1a2e', padding: '11px 18px' }}>
            <span style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>🏦 Movimientos importados — {bodega.nombre}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['Fecha', 'Tipo', 'Descripción', 'Monto'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: h === 'Monto' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(datos.dias || []).flatMap(d => {
                  const rows = [];
                  if (d.extracto.mastercard > 0) rows.push({ fecha: d.fecha, tipo: 'mastercard', desc: 'Abono ventas netas Mastercard', monto: d.extracto.mastercard });
                  if (d.extracto.visa > 0) rows.push({ fecha: d.fecha, tipo: 'visa', desc: 'Abono ventas netas Visa', monto: d.extracto.visa });
                  if (d.extracto.amex > 0) rows.push({ fecha: d.fecha, tipo: 'amex', desc: 'NC Amex Ventas Netas', monto: d.extracto.amex });
                  if (d.extracto.qr > 0) rows.push({ fecha: d.fecha, tipo: 'qr', desc: 'Pago A Llave De Comercio', monto: d.extracto.qr });
                  return rows;
                }).map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                    <td style={{ padding: '9px 14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {format(new Date(r.fecha + 'T12:00'), "d MMM", { locale: es })}
                    </td>
                    <td style={{ padding: '9px 14px' }}><TipoBadge tipo={r.tipo} /></td>
                    <td style={{ padding: '9px 14px', color: '#6B7280', fontSize: 12 }}>{r.desc}</td>
                    <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{COP(r.monto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


    </div>
  );
}

// ── Componente principal ──────────────────────────────────
export default function ConciliacionBancaria() {
  const hoy = new Date();
  const mesDefault = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
  const [mesActual, setMesActual] = useState(mesDefault);
  const [bodegas, setBodegas]     = useState([]);
  const [tabActiva, setTabActiva] = useState(null);

  useEffect(() => {
    API.get('/bodegas')
      .then(r => {
        const pvs = r.data.filter(b => b.tipo === 'punto_venta');
        setBodegas(pvs);
        if (pvs.length) setTabActiva(pvs[0].id);
      })
      .catch(() => {});
  }, []);

  const MESES = (() => {
    const meses = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      meses.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      d.setMonth(d.getMonth() - 1);
    }
    return meses;
  })();

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>🏦 Conciliación Bancaria</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>
          Extracto bancario vs ventas en sistema — por punto de venta
        </p>
      </div>

      {/* Selector mes + leyenda */}
      <div style={{ background: 'white', borderRadius: 12, padding: '14px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 20, display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Mes</div>
          <select value={mesActual} onChange={e => setMesActual(e.target.value)}
            style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' }}>
            {MESES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', background: '#F9FAFB', padding: '10px 14px', borderRadius: 8, lineHeight: 1.7 }}>
          <div>💳 Tarjetas: comisión <strong>3.8%</strong> descontada en extracto</div>
          <div>📅 Tarjeta: venta día X aparece en extracto el día X+1</div>
          <div>📱 QR / Llave Davivienda: mismo día</div>
        </div>
      </div>

      {/* Tabs por PDV */}
      {bodegas.length > 0 && (
        <>
          <div style={{ display: 'flex', borderBottom: '2px solid #E5E7EB', marginBottom: 0 }}>
            {bodegas.map(b => (
              <button key={b.id} onClick={() => setTabActiva(b.id)} style={{
                padding: '10px 22px', border: 'none', cursor: 'pointer', fontSize: 13, background: 'transparent',
                fontWeight: tabActiva === b.id ? 800 : 500,
                color: tabActiva === b.id ? '#1a1a2e' : '#6B7280',
                borderBottom: tabActiva === b.id ? '2px solid #1a1a2e' : '2px solid transparent',
                marginBottom: -2,
              }}>
                🏪 {b.nombre}
              </button>
            ))}
          </div>
          <div style={{ paddingTop: 20 }}>
            {bodegas.map(b => (
              <div key={b.id} style={{ display: tabActiva === b.id ? 'block' : 'none' }}>
                <PanelPDV bodega={b} mesActual={mesActual} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DifBadge({ val, cuadra }) {
  if (val === undefined || val === null) return <span style={{ color: '#D1D5DB' }}>—</span>;
  if (cuadra) return <span style={{ fontSize: 14 }}>✅</span>;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
      background: val < 0 ? '#FEE2E2' : '#FEF3C7',
      color: val < 0 ? '#991B1B' : '#92400E'
    }}>
      {val > 0 ? '+' : ''}{COP(val)}
    </span>
  );
}

function TipoBadge({ tipo }) {
  const map = {
    mastercard: { label: 'Mastercard', bg: '#EEF2FF', color: '#3730A3' },
    visa:       { label: 'Visa',       bg: '#DBEAFE', color: '#1E40AF' },
    amex:       { label: 'Amex',       bg: '#EDE9FE', color: '#5B21B6' },
    qr:         { label: 'QR / Llave', bg: '#E0F2FE', color: '#0369A1' },
  };
  const s = map[tipo] || { label: tipo, bg: '#F3F4F6', color: '#6B7280' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}
